import { Id, Store } from '@nextforce/api'
import { StackNavigationProp } from '@react-navigation/stack'
import { Divider, Text, makeStyles } from '@rneui/themed'
import { t } from 'app/i18l'
import { HandleNavigationProp } from 'app/routing_context'
import { HomeStackParamList } from 'home/stack/types'
import React from 'react'
import { View } from 'react-native'
import {
  DropdownItem,
  DropdownItemComponentProps,
} from 'shared/components/dropdown'
import { StoreItem } from 'shared/components/store_item'
import { showHeaderButtonDropdown } from 'shared/header/header_button_dropdown'

const MORE_ITEM_ID = 'MORE_ITEM_ID'
const SHOW_ALL_STORES_ITEM_ID = 'SHOW_ALL_STORES_ITEM_ID'
export interface StoreDropdownItem extends DropdownItem {
  store?: Store
}

interface showStoreHeaderDropdownProps {
  stores: Store[] | undefined
  activeStoreId: Id | undefined
  setActiveStoreId?: (id: Id | undefined) => void
  setAllStoresSelected?: (selected: boolean) => void
  allStoresSelected?: boolean | undefined
  showAllStores?: boolean
  recentStores: Store[]
}

export function showStoreHeaderDropdown<
  RouteName extends keyof HomeStackParamList
>(
  navigation: StackNavigationProp<HomeStackParamList, RouteName>,
  handleNavigation: <Route extends keyof HomeStackParamList>({
    routeName,
    routeParams,
  }: HandleNavigationProp<Route>) => void,
  {
    stores,
    recentStores,
    activeStoreId,
    setActiveStoreId,
    setAllStoresSelected,
    allStoresSelected,
  }: showStoreHeaderDropdownProps,
  onStoreSelected?: (id: Id) => void
) {
  const hideDownArrow = true
  return showHeaderButtonDropdown(
    navigation,
    getStoreSections(),
    allStoresSelected && setAllStoresSelected
      ? SHOW_ALL_STORES_ITEM_ID
      : activeStoreId,
    onSelection,
    DropdownStoreItemComponent,
    hideDownArrow
  )

  function getStoreSections() {
    if (!stores || !activeStoreId) {
      return []
    }

    const items: StoreDropdownItem[] = recentStores.map(s => ({
      name: `${s.storeNumber}`,
      store: s,
      id: s.id,
    }))

    if (setAllStoresSelected) {
      items.push({
        name: t('allStores'),
        id: SHOW_ALL_STORES_ITEM_ID,
      })
    }

    if (stores.length > recentStores.length) {
      items.push({
        name: t('chooseStore'),
        id: MORE_ITEM_ID,
      })
    }

    return [
      {
        data: items,
      },
    ]
  }

  function onSelection(id: Id) {
    if (id === MORE_ITEM_ID) {
      handleNavigation({
        routeName: 'store_chooser',
      })
      return
    }

    if (id === SHOW_ALL_STORES_ITEM_ID && setAllStoresSelected) {
      setAllStoresSelected(true)
      return
    }
    setAllStoresSelected?.(false)
    setActiveStoreId?.(id)
    onStoreSelected?.(id)
  }

  function DropdownStoreItemComponent({
    item,
    selected,
  }: DropdownItemComponentProps<StoreDropdownItem>) {
    const styles = useStyles()
    const showAllStoresOptionName =
      item.id === 'SHOW_ALL_STORES_ITEM_ID' ? item.name : undefined
    if (item.id === 'MORE_ITEM_ID') {
      return (
        <View style={styles.item}>
          <Divider />
          <Text style={styles.moreText}>{item.name}</Text>
        </View>
      )
    }

    return (
      <View style={styles.item}>
        <StoreItem
          store={item.store}
          isSelected={selected}
          showAllStoresOptionName={showAllStoresOptionName}
        />
      </View>
    )
  }
}

const useStyles = makeStyles(theme => ({
  item: {
    flex: 1,
    marginRight: 20,
    width: 200,
  },
  moreText: {
    marginLeft: 10,
    marginTop: 15,
    fontSize: 17,
  },
}))
